import { Button, Input, Select, toast } from "@gotts.ai/ui";
import {
  ArrowRight,
  Boxes,
  Calendar,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useState } from "react";

import { useRpcRequest } from "../../hooks/useRpcRequest";

import { ConfirmModal } from "./ConfirmModal";

import type { ChainControlSharedProps } from "./types";

const TIME_UNITS = [
  { label: "Seconds", value: "seconds" },
  { label: "Minutes", value: "minutes" },
  { label: "Hours", value: "hours" },
  { label: "Days", value: "days" },
  { label: "Weeks", value: "weeks" },
] as const;

type TimeUnit = (typeof TIME_UNITS)[number]["value"];

const TIME_MULTIPLIERS: Record<TimeUnit, number> = {
  seconds: 1,
  minutes: 60,
  hours: 3600,
  days: 86400,
  weeks: 604800,
};

function formatPreviewTime(ts: number): string {
  const d = new Date(ts * 1000);
  return (
    d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }) +
    " " +
    d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    })
  );
}

export function TimeControls({
  loading,
  withLoading,
  onAction,
  simulatedTimestamp,
}: ChainControlSharedProps) {
  const { request } = useRpcRequest();
  const [customTimeValue, setCustomTimeValue] = useState("");
  const [customTimeUnit, setCustomTimeUnit] = useState<TimeUnit>("hours");
  const [showAdvanceToDate, setShowAdvanceToDate] = useState(false);
  const [showAdvanceToBlock, setShowAdvanceToBlock] = useState(false);
  const [targetDate, setTargetDate] = useState("");
  const [targetBlock, setTargetBlock] = useState("");

  // ConfirmModal state for large time jumps
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAdvance, setPendingAdvance] = useState<{
    seconds: number;
    label: string;
  } | null>(null);

  const customSeconds =
    parseInt(customTimeValue, 10) > 0
      ? parseInt(customTimeValue, 10) * TIME_MULTIPLIERS[customTimeUnit]
      : 0;

  async function handleCustomTimeAdvance() {
    const value = parseInt(customTimeValue, 10);
    if (!value || value <= 0) return;
    const totalSeconds = value * TIME_MULTIPLIERS[customTimeUnit];
    await withLoading("customTime", async () => {
      await request("evm_increaseTime", [totalSeconds]);
      await request("evm_mine");
      toast.success(
        `Advanced ${String(value)} ${customTimeUnit} (${totalSeconds.toLocaleString()}s)`,
      );
      onAction?.(`+${String(value)} ${customTimeUnit}`);
    });
  }

  function handlePresetAdvance(
    seconds: number,
    label: string,
    needsConfirm: boolean,
  ) {
    if (needsConfirm) {
      setPendingAdvance({ seconds, label });
      setConfirmOpen(true);
      return;
    }
    void executeAdvance(seconds, label);
  }

  async function executeAdvance(seconds: number, label: string) {
    await withLoading(label, async () => {
      // Auto-snapshot before large advances (>= 1 week)
      if (seconds >= 604800) {
        await request("evm_snapshot");
      }
      await request("evm_increaseTime", [seconds]);
      await request("evm_mine");
      toast.success(`Advanced ${label}`);
      onAction?.(label);
    });
  }

  function handleConfirm() {
    if (!pendingAdvance) return;
    setConfirmOpen(false);
    void executeAdvance(pendingAdvance.seconds, pendingAdvance.label);
    setPendingAdvance(null);
  }

  function handleConfirmCancel() {
    setConfirmOpen(false);
    setPendingAdvance(null);
  }

  async function handleAdvanceToDate() {
    if (
      !targetDate ||
      simulatedTimestamp === null ||
      simulatedTimestamp === undefined
    )
      return;
    const targetTs = Math.floor(new Date(targetDate).getTime() / 1000);
    const delta = targetTs - simulatedTimestamp;
    if (delta <= 0) {
      toast.error("Target date must be in the future relative to chain time");
      return;
    }
    await withLoading("advanceToDate", async () => {
      await request("evm_increaseTime", [delta]);
      await request("evm_mine");
      toast.success(
        `Advanced to ${new Date(targetTs * 1000).toLocaleDateString()}`,
      );
      onAction?.("Advance to date");
    });
  }

  async function handleAdvanceToBlock() {
    const target = parseInt(targetBlock, 10);
    if (!target || target <= 0) return;
    await withLoading("advanceToBlock", async () => {
      // Get current block
      const currentHex = (await request("eth_blockNumber")) as string;
      const current = parseInt(currentHex, 16);
      const count = target - current;
      if (count <= 0) {
        toast.error("Target block must be greater than current block");
        return;
      }
      for (let i = 0; i < count; i++) {
        await request("evm_mine");
      }
      toast.success(
        `Mined ${String(count)} blocks to reach block ${String(target)}`,
      );
      onAction?.(`Mine to block ${String(target)}`);
    });
  }

  // Compute resulting timestamp for confirm modal detail
  const confirmDetail =
    pendingAdvance &&
    simulatedTimestamp !== null &&
    simulatedTimestamp !== undefined
      ? `Chain time: ${formatPreviewTime(simulatedTimestamp)} \u2192 ${formatPreviewTime(simulatedTimestamp + pendingAdvance.seconds)}${pendingAdvance.seconds >= 604800 ? "\nAuto-snapshot will be taken before advancing." : ""}`
      : undefined;

  return (
    <div className="space-y-4">
      {/* Custom advance */}
      <div className="flex gap-2">
        <div className="flex flex-1 overflow-hidden rounded-sm border border-[var(--glass-border)]">
          <Input
            type="number"
            placeholder="Amount"
            value={customTimeValue}
            onChange={(e) => {
              setCustomTimeValue(e.target.value);
            }}
            min={1}
            className="w-24 border-0"
          />
          <Select
            options={TIME_UNITS.map((u) => ({
              label: u.label,
              value: u.value,
            }))}
            value={customTimeUnit}
            onValueChange={(v) => {
              setCustomTimeUnit(v as TimeUnit);
            }}
            className="w-28 border-0 border-l border-[var(--glass-border)]"
          />
        </div>
        <Button
          variant="gold"
          size="sm"
          onClick={handleCustomTimeAdvance}
          loading={loading === "customTime"}
          disabled={
            loading !== null ||
            !customTimeValue ||
            parseInt(customTimeValue, 10) <= 0
          }
        >
          Advance
        </Button>
      </div>

      {/* Preview */}
      {simulatedTimestamp !== null &&
        simulatedTimestamp !== undefined &&
        customSeconds > 0 && (
          <p className="font-mono text-[10px] text-[var(--color-text-muted)]">
            {formatPreviewTime(simulatedTimestamp)}{" "}
            <ArrowRight
              size={10}
              className="mx-0.5 inline-block align-middle"
            />{" "}
            {formatPreviewTime(simulatedTimestamp + customSeconds)}
          </p>
        )}

      {/* Preset buttons — visual escalation from safe to risky */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <button
          type="button"
          onClick={() => {
            handlePresetAdvance(3600, "+1 Hour", false);
          }}
          disabled={loading !== null}
          className="border-[var(--color-success)]/20 bg-[var(--color-success)]/5 hover:border-[var(--color-success)]/40 hover:bg-[var(--color-success)]/10 flex h-10 items-center justify-center rounded-sm border font-mono text-xs text-[var(--color-success)] transition-all disabled:opacity-40"
        >
          {loading === "+1 Hour" ? "..." : "+1 Hour"}
        </button>
        <button
          type="button"
          onClick={() => {
            handlePresetAdvance(86400, "+1 Day", false);
          }}
          disabled={loading !== null}
          className="hover:border-[var(--color-gold)]/30 flex h-10 items-center justify-center rounded-sm border border-[var(--glass-border)] bg-[rgba(255,255,255,0.02)] font-mono text-xs text-[var(--color-text-dim)] transition-all hover:text-[var(--color-gold)] disabled:opacity-40"
        >
          {loading === "+1 Day" ? "..." : "+1 Day"}
        </button>
        <button
          type="button"
          onClick={() => {
            handlePresetAdvance(604800, "+1 Week", true);
          }}
          disabled={loading !== null}
          className="border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 hover:border-[var(--color-warning)]/40 hover:bg-[var(--color-warning)]/10 flex h-10 items-center justify-center rounded-sm border font-mono text-xs text-[var(--color-warning)] transition-all disabled:opacity-40"
        >
          {loading === "+1 Week" ? "..." : "+1 Week"}
        </button>
        <button
          type="button"
          onClick={() => {
            handlePresetAdvance(2592000, "+30 Days", true);
          }}
          disabled={loading !== null}
          className="border-[var(--color-error)]/20 bg-[var(--color-error)]/5 hover:border-[var(--color-error)]/40 hover:bg-[var(--color-error)]/10 flex h-10 items-center justify-center rounded-sm border font-mono text-xs text-[var(--color-error)] transition-all disabled:opacity-40"
        >
          {loading === "+30 Days" ? "..." : "+30 Days"}
        </button>
      </div>

      <p className="text-center text-[9px] text-[var(--color-text-muted)]">
        +1 Week and +30 Days auto-snapshot before advancing
      </p>

      {/* Advance to Date (collapsible) */}
      <div>
        <button
          type="button"
          onClick={() => {
            setShowAdvanceToDate((v) => !v);
          }}
          className="flex items-center gap-1.5 text-[11px] text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-text-dim)]"
        >
          {showAdvanceToDate ? (
            <ChevronDown size={12} />
          ) : (
            <ChevronRight size={12} />
          )}
          <Calendar size={12} />
          Advance to Date
          {!showAdvanceToDate && targetDate && (
            <span className="ml-1 text-[var(--color-text-dim)]">
              (
              {new Date(targetDate).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
              )
            </span>
          )}
        </button>
        {showAdvanceToDate && (
          <div className="mt-2 flex gap-2">
            <input
              type="datetime-local"
              value={targetDate}
              onChange={(e) => {
                setTargetDate(e.target.value);
              }}
              className="flex-1 rounded-sm border border-[var(--glass-border)] bg-[rgba(255,255,255,0.02)] px-2 py-1.5 text-xs text-white"
            />
            <Button
              variant="gold"
              size="sm"
              onClick={handleAdvanceToDate}
              loading={loading === "advanceToDate"}
              disabled={
                loading !== null || !targetDate || simulatedTimestamp === null
              }
            >
              Go
            </Button>
          </div>
        )}
      </div>

      {/* Advance to Block (collapsible) */}
      <div>
        <button
          type="button"
          onClick={() => {
            setShowAdvanceToBlock((v) => !v);
          }}
          className="flex items-center gap-1.5 text-[11px] text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-text-dim)]"
        >
          {showAdvanceToBlock ? (
            <ChevronDown size={12} />
          ) : (
            <ChevronRight size={12} />
          )}
          <Boxes size={12} />
          Advance to Block
          {!showAdvanceToBlock && targetBlock && (
            <span className="ml-1 text-[var(--color-text-dim)]">
              (Block #{targetBlock})
            </span>
          )}
        </button>
        {showAdvanceToBlock && (
          <div className="mt-2 flex gap-2">
            <Input
              type="number"
              placeholder="Target block number"
              value={targetBlock}
              onChange={(e) => {
                setTargetBlock(e.target.value);
              }}
              min={1}
              className="flex-1"
            />
            <Button
              variant="gold"
              size="sm"
              onClick={handleAdvanceToBlock}
              loading={loading === "advanceToBlock"}
              disabled={
                loading !== null ||
                !targetBlock ||
                parseInt(targetBlock, 10) <= 0
              }
            >
              Go
            </Button>
          </div>
        )}
      </div>

      {/* Confirm modal for large time jumps */}
      <ConfirmModal
        open={confirmOpen}
        onClose={handleConfirmCancel}
        onConfirm={handleConfirm}
        title={`Advance ${pendingAdvance?.label ?? ""}?`}
        description="This is a large time jump that may affect time-dependent contracts."
        detail={confirmDetail}
        confirmLabel={pendingAdvance?.label ?? "Advance"}
        confirmVariant="warning"
      />
    </div>
  );
}
